import React, { useEffect, useMemo, useState } from "react";
import { TitleBadge } from "./TitleBadge";
import DotsPatternBackground from "../../assets/dots-bg.svg";
import CarouselFrame from "../../assets/carousel-frame.svg";
import BasketballCourt from "../../assets/basketball-frame.avif";
import VolleyballCourt from "../../assets/volleyball-frame.avif";
import IceHockeyRink from "../../assets/icehockey-frame.avif";
import FootballCourt from "../../assets/football-frame.avif";
import BaseballCourt from "../../assets/baseball-frame.avif";
import TennisCourt from "../../assets/tennis-frame.avif";
import { ReactComponent as TitleFrame } from "../../assets/title-frame.svg";

type SportSlide = {
  name: string;
  accentColor: string;
  image: string;
};

type SlidePlacement = "active" | "prev" | "next" | "off-left" | "off-right";

const AUTO_SLIDE_INTERVAL = 5000;

const SPORT_SLIDES: SportSlide[] = [
  {
    name: "Football",
    accentColor: "#5FAD35",
    image: FootballCourt,
  },
  {
    name: "Basketball",
    accentColor: "#C28947",
    image: BasketballCourt,
  },
  {
    name: "Volleyball",
    accentColor: "#BD442D",
    image: VolleyballCourt,
  },
  {
    name: "Tennis",
    accentColor: "#336C21",
    image: TennisCourt,
  },
  {
    name: "Ice Hockey",
    accentColor: "#005785",
    image: IceHockeyRink,
  },
  {
    name: "Baseball",
    accentColor: "#BC733B",
    image: BaseballCourt,
  },
];

export const SportsSlider = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.matchMedia("(min-width: 1024px)").matches;
  });
  const slideCount = SPORT_SLIDES.length;

  const goToSlide = (nextIndex: number) => {
    const normalized = (nextIndex + slideCount) % slideCount;
    setActiveIndex(normalized);
  };

  useEffect(() => {
    if (isPaused) {
      return;
    }

    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % slideCount);
    }, AUTO_SLIDE_INTERVAL);

    return () => window.clearInterval(timer);
  }, [isPaused, slideCount]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const mediaQuery = window.matchMedia("(min-width: 1024px)");
    const handleChange = (event: MediaQueryListEvent) => {
      setIsLargeScreen(event.matches);
    };

    setIsLargeScreen(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  const placementConfig = useMemo(() => {
    const desktopTranslations = {
      prev: "-160%",
      next: "60%",
      offLeft: "-255%",
      offRight: "155%",
    };

    const mobileTranslations = {
      prev: "-135%",
      next: "35%",
      offLeft: "-205%",
      offRight: "115%",
    };

    const { prev, next, offLeft, offRight } = isLargeScreen
      ? desktopTranslations
      : mobileTranslations;

    return {
      active: {
        transform: "translate(-50%, -50%)",
        zIndex: 40,
      },
      prev: {
        transform: `translate(${prev}, -50%)`,
        zIndex: 25,
      },
      next: {
        transform: `translate(${next}, -50%)`,
        zIndex: 25,
      },
      "off-left": {
        transform: `translate(${offLeft}, -50%)`,
        opacity: 0,
        zIndex: 10,
      },
      "off-right": {
        transform: `translate(${offRight}, -50%)`,
        opacity: 0,
        zIndex: 10,
      },
    } as Record<SlidePlacement, React.CSSProperties>;
  }, [isLargeScreen]);

  const resolvePlacement = (index: number): SlidePlacement => {
    const delta = (index - activeIndex + slideCount) % slideCount;
    if (delta === 0) return "active";
    if (delta === 1) return "next";
    if (delta === slideCount - 1) return "prev";
    return delta < slideCount / 2 ? "off-right" : "off-left";
  };

  return (
    <section className="relative w-full py-16 lg:py-28 text-white overflow-hidden">
      <div className="flex flex-col items-center gap-4 lg:gap-9 text-center px-3 md:px-5 lg:px-10 2xl:px-20">
        <TitleBadge title="Follow All Sports" />
        <p className="text-sm md:text-xl font-medium text-gray-200">
          Follow not just soccer, but every action across basketball,
          volleyball, tennis, baseball, and ice hockey. Keep your broadcast in
          sync even when the schedule is packed with overlapping events.
        </p>
      </div>
      <div className="mt-10 lg:mt-20 flex flex-col items-center gap-4 lg:gap-6 2xl:gap-12 w-full">
        <img
          src={DotsPatternBackground}
          alt=""
          aria-hidden
          className="hidden lg:block pointer-events-none absolute left-1/2 top-[45%] h-80 w-screen -translate-x-1/2 -z-10 object-cover opacity-40"
        />
        <div
          className="relative w-full h-[180px] lg:h-[360px] 2xl:h-[520px]"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {SPORT_SLIDES.map(({ name, accentColor, image }, index) => {
            const placement = resolvePlacement(index);
            const styles = placementConfig[placement];
            const isActive = placement === "active";

            return (
              <article
                key={name}
                className="absolute left-1/2 top-1/2 w-full max-w-4xl transition-all duration-700 ease-out"
                style={{
                  ...styles,
                  pointerEvents: isActive ? "auto" : "none",
                }}
                aria-hidden={!isActive}
              >
                <img
                  src={CarouselFrame}
                  alt=""
                  aria-hidden
                  className="pointer-events-none block w-full select-none object-contain h-[180px] lg:h-[360px] 2xl:h-[520px]"
                  loading={index === 0 ? "eager" : "lazy"}
                />
                <div className="absolute inset-0 z-10 flex justify-center px-4 pt-4 sm:px-8 sm:pt-8 lg:px-12 lg:pt-12">
                  <img
                    src={image}
                    alt={`${name} court illustration`}
                    className="h-32 w-full max-w-64 lg:max-w-lg 2xl:max-w-3xl object-cover lg:h-64 2xl:h-[380px]"
                    loading={index === 0 ? "eager" : "lazy"}
                  />
                </div>
                <div className="pointer-events-none absolute left-1/2 -bottom-6 lg:-bottom-9 z-20 -translate-x-1/2 w-56 lg:w-80 2xl:w-[491px]">
                  <TitleFrame
                    className="h-11 lg:h-16 2xl:h-20 w-full drop-shadow-[0_10px_30px_rgba(0,0,0,0.25)]"
                    style={{ color: accentColor }}
                    aria-hidden
                    focusable="false"
                  />
                  <span className="absolute inset-0 flex items-center justify-center px-6 text-center text-base font-extrabold lg:text-3xl">
                    {name}
                  </span>
                </div>
              </article>
            );
          })}
        </div>
        <div className="flex w-full px-5 items-center justify-between gap-6 mt-10 lg:max-w-96">
          <button
            type="button"
            onClick={() => goToSlide(activeIndex - 1)}
            className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-950 border border-border text-white transition hover:border-white"
            aria-label="Previous sport"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-5 w-5"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <div className="flex items-center gap-2">
            {SPORT_SLIDES.map((slide, index) => (
              <button
                key={slide.name}
                className={`h-2 rounded-full transition-all ${
                  activeIndex === index
                    ? "w-6 bg-white"
                    : "w-2 bg-white/40 hover:bg-white/70"
                }`}
                onClick={() => goToSlide(index)}
                aria-label={`Show ${slide.name}`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => goToSlide(activeIndex + 1)}
            className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-900 border border-border text-white transition hover:border-white"
            aria-label="Next sport"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-5 w-5"
            >
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};
